import { memo } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';

function ClipBulkDownloadDialog({
  isOpen,
  onClose,
  onConfirm,
  downloadableClips,
  nonDownloadableClips,
  isLoading
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  downloadableClips: Clip[];
  nonDownloadableClips: Clip[];
  isLoading: boolean;
}) {
  return (
    <ContentDialog
      isOpen={isOpen}
      setIsOpen={open => {
        if (!open) {
          onClose();
        }
      }}
      title="Download clips"
      titleSize="medium"
      size="medium"
      disableBackdropClose={isLoading}
    >
      <div className="flex flex-col">
        <div className="grow px-5 pb-4 pt-2">
          <p className="text-sm leading-tight text-slate-600">
            {nonDownloadableClips.length > 0
              ? `${downloadableClips.length} clip(s) will be queued for download. ${nonDownloadableClips.length} clip(s) are not ready and will be skipped.`
              : `${downloadableClips.length} clip(s) will be queued for download.`}
          </p>

          {downloadableClips.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Will be downloaded</div>
              {downloadableClips.map(clip => (
                <div key={clip.id} className="flex items-center gap-2 py-2">
                  <Icon name="IconCheck" size={14} className="shrink-0 text-green-500" />
                  <span className="truncate text-sm text-slate-800">{clip.title}</span>
                </div>
              ))}
            </div>
          )}

          {nonDownloadableClips.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                Not ready — will be skipped
              </div>
              {nonDownloadableClips.map(clip => (
                <div key={clip.id} className="flex items-center gap-2 py-2">
                  <Icon name="IconX" size={14} className="shrink-0 text-slate-400" />
                  <span className="truncate text-sm text-slate-400">{clip.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-t-slate-200 px-5 pb-3 pt-2.5">
          <Button variation="outline" trackingId="bulk-download-dialog-cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button variation="filled" trackingId="bulk-download-dialog-confirm" disabled={isLoading} onClick={onConfirm}>
            {isLoading ? (
              <Icon name="IconLoader2" className="animate-spin" size={16} />
            ) : (
              <>
                Download {downloadableClips.length} clip{downloadableClips.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      </div>
    </ContentDialog>
  );
}

export default memo(ClipBulkDownloadDialog);
